import AddBoxIcon from '@mui/icons-material/AddBox';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import MaterialTable, { Icons, MaterialTableProps } from 'material-table';
import React from 'react';
import { Toolbar, ToolbarProps } from './components/Toolbar';
import { StyledTable } from './styled';

const tableIcons: Icons = {
  Add: React.forwardRef((props, ref) => <AddBoxIcon {...props} ref={ref} />),
  Check: React.forwardRef((props, ref) => <CheckIcon {...props} ref={ref} />),
  Clear: React.forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
  Delete: React.forwardRef((props, ref) => <DeleteOutlineIcon {...props} ref={ref} />),
  DetailPanel: React.forwardRef((props, ref) => <ChevronRightIcon {...props} ref={ref} />),
  Edit: React.forwardRef((props, ref) => <EditIcon {...props} ref={ref} />),
  Export: React.forwardRef((props, ref) => <SaveAltIcon {...props} ref={ref} />),
  Filter: React.forwardRef((props, ref) => <FilterListIcon {...props} ref={ref} />),
  FirstPage: React.forwardRef((props, ref) => <FirstPageIcon {...props} ref={ref} />),
  LastPage: React.forwardRef((props, ref) => <LastPageIcon {...props} ref={ref} />),
  NextPage: React.forwardRef((props, ref) => <ChevronRightIcon {...props} ref={ref} />),
  PreviousPage: React.forwardRef((props, ref) => <ChevronLeftIcon {...props} ref={ref} />),
  ResetSearch: React.forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
  Search: React.forwardRef((props, ref) => <SearchIcon {...props} ref={ref} />),
  SortArrow: React.forwardRef((props, ref) => <ArrowUpwardIcon {...props} ref={ref} />),
  ThirdStateCheck: React.forwardRef((props, ref) => <RemoveIcon {...props} ref={ref} />),
  ViewColumn: React.forwardRef((props, ref) => <ViewColumnIcon {...props} ref={ref} />),
};

export type DataTableProps<Data extends TableDataAttributes | TableDataClass> = {
  toolbarProps?: Omit<ToolbarProps, 'tableProps'>;
} & MaterialTableProps<Data>;

type TableDataAttributes = Record<string, unknown>;

export class TableDataClass {}

export type TableData = TableDataAttributes | TableDataClass;

function DataTable<Data extends TableData>({
  toolbarProps,
  components,
  icons,
  options,
  ...rest
}: DataTableProps<Data>): React.ReactElement {
  const toolbarPropsRef = React.useRef(toolbarProps);
  toolbarPropsRef.current = toolbarProps;

  const tableComponents = React.useMemo(
    () => ({
      Container: (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />,
      Toolbar: (props: MaterialTableProps<TableData>) => <Toolbar {...toolbarPropsRef.current} tableProps={props} />,
      ...components,
    }),
    [components],
  );

  return (
    <StyledTable>
      <MaterialTable
        components={tableComponents}
        icons={{ ...tableIcons, ...icons }}
        options={{
          ...options,
          showTitle: false,
          pageSize: 10,
          pageSizeOptions: [10, 25, 50],
          searchFieldAlignment: 'left',
        }}
        {...rest}
      />
    </StyledTable>
  );
}

DataTable.displayName = 'DataTable';

export { DataTable };
